import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Check, X, Sparkles, Zap, Trophy, Crown, Gift, TrendingUp, Bot,
  ArrowRight, Rocket,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLANS = [
  {
    id: "free", name: "Free", price: 0, priceLabel: "$0", cadence: "forever",
    icon: Sparkles, accent: "hsl(var(--foreground))",
    tagline: "Learn to trade with zero risk",
    features: [
      { in: true,  t: "Paper trading with $100k virtual" },
      { in: true,  t: "Chart Copilot · 10 setup scans / day" },
      { in: true,  t: "1 AI bot (paper mode)" },
      { in: true,  t: "AI Coach coach · 10 messages / day" },
      { in: true,  t: "Follow up to 3 copy-traders" },
      { in: true,  t: "Full security (2FA, audit log)" },
      { in: false, t: "Live trading via IBKR / Alpaca" },
      { in: false, t: "Unlimited Copilot + Setups Today" },
      { in: false, t: "Publisher revenue share" },
    ],
    cta: "Start free",
    ctaLink: "/register",
  },
  {
    id: "plus", name: "Plus", price: 14, priceLabel: "$14", cadence: "/mo",
    yearly: 119, yearlyLabel: "$119/yr", yearlySave: "save 29%",
    icon: Rocket, accent: "hsl(var(--ai))",
    tagline: "Trade real money with confidence",
    highlight: true,
    badge: "Most popular",
    features: [
      { in: true, t: "Everything in Free" },
      { in: true, t: "Live trading · IBKR / Alpaca / Tradier" },
      { in: true, t: "Unlimited Copilot scans + real-time Setups Today" },
      { in: true, t: "10 AI bots (live money enabled)" },
      { in: true, t: "Unlimited AI Coach messages" },
      { in: true, t: "Push alerts for new setups + IBKR reauth" },
      { in: true, t: "Backtesting + advanced orders" },
      { in: true, t: "Behavioral bias nudges before every trade" },
      { in: true, t: "Priority chat support" },
    ],
    cta: "Get Plus",
    ctaAction: "join",
  },
  {
    id: "elite", name: "Elite", price: 39, priceLabel: "$39", cadence: "/mo",
    yearly: 349, yearlyLabel: "$349/yr", yearlySave: "save 25%",
    icon: Crown, accent: "hsl(var(--gain))",
    tagline: "Publish bots. Earn from your skill.",
    features: [
      { in: true, t: "Everything in Plus" },
      { in: true, t: "Unlimited AI bots" },
      { in: true, t: "Become a Publisher · 50% revenue share" },
      { in: true, t: "Multi-leg options + Greeks + Portfolio VaR" },
      { in: true, t: "Voice Coach (read-aloud plans)" },
      { in: true, t: "API access + custom pattern training" },
      { in: true, t: "White-label bot profile pages" },
      { in: true, t: "Team accounts (up to 3 seats)" },
      { in: true, t: "1-on-1 monthly strategy call" },
    ],
    cta: "Get Elite",
    ctaAction: "join",
  },
];

const REPLACES = [
  { name: "TradingView Premium", price: 60 },
  { name: "Trade Ideas Basic", price: 127 },
  { name: "Benzinga Pro", price: 37 },
  { name: "Composer Auto-trader", price: 30 },
  { name: "TrendSpider", price: 47 },
];

export default function Pricing() {
  const [cadence, setCadence] = useState("monthly"); // monthly | yearly
  const [busy, setBusy] = useState(null);
  const { isOwner } = useAuth();

  const joinWaitlist = async (planId) => {
    setBusy(planId);
    try {
      await api.post("/pricing/waitlist", { plan: planId });
      toast.success(`You're on the ${planId.toUpperCase()} waitlist — we'll email you when it opens`);
    } catch { toast.error("Couldn't save — try again"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-10" data-testid="pricing-page">
      {isOwner && (
        <Card className="p-4 rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/[0.06] max-w-4xl mx-auto"
              data-testid="pricing-owner-banner">
          <div className="flex items-start gap-3 flex-wrap">
            <Badge className="border-0 bg-emerald-500/20 text-emerald-300 font-mono-tab uppercase text-[10px] tracking-wider">
              Owner · unlimited
            </Badge>
            <div className="flex-1 min-w-[240px]">
              <div className="font-display font-semibold">You already have Elite access on the house</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Every paid feature is unlocked on your account — you're viewing this page as a preview of what non-owner users see. No upgrade needed.
              </div>
            </div>
          </div>
        </Card>
      )}
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto pt-4">
        <div className="flex items-center justify-center mb-4">
          <img src="/brand/aanar-mark-reversed.svg" alt="Aanar" className="w-16 h-16 dark:block hidden" />
          <img src="/brand/aanar-mark.svg" alt="Aanar" className="w-16 h-16 dark:hidden block" />
        </div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Pricing</div>
        <h1 className="font-display text-4xl md:text-6xl font-black mt-2 leading-[0.95]">
          The <span className="text-[hsl(var(--ai))]">A</span> in Aanar<br />
          is a <span className="text-[hsl(var(--gain))]">price line</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mt-4 leading-relaxed">
          Because that's the whole app in one glyph — read the chart, know the setup, trade the plan.
          <span className="text-foreground font-semibold"> Aanar replaces $301/mo of trading tools</span> with a single subscription that
          <span className="text-foreground font-semibold"> actually helps you win</span>. No PFOF. No hidden fees.
        </p>
      </div>

      {/* "Replaces" strip */}
      <Card className="p-5 rounded-2xl max-w-4xl mx-auto" data-testid="pricing-replaces">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground text-center mb-3">One Aanar Plus subscription replaces</div>
        <div className="flex items-center justify-center flex-wrap gap-3">
          {REPLACES.map((r) => (
            <div key={r.name} className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground line-through">{r.name}</span>
              <span className="text-[hsl(var(--loss))] font-mono-tab">${r.price}/mo</span>
            </div>
          ))}
        </div>
        <div className="text-center mt-3 font-mono-tab text-sm">
          Total: <span className="text-muted-foreground line-through">${REPLACES.reduce((a, b) => a + b.price, 0)}/mo</span>
          <span className="text-[hsl(var(--gain))] font-bold ml-2">→ Aanar $14/mo</span>
        </div>
      </Card>

      {/* Cadence toggle */}
      <div className="flex justify-center">
        <div className="inline-flex bg-secondary rounded-full p-1 text-sm">
          <button onClick={() => setCadence("monthly")}
                  data-testid="pricing-monthly"
                  className={`px-4 py-1.5 rounded-full font-medium transition-colors ${cadence === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
            Monthly
          </button>
          <button onClick={() => setCadence("yearly")}
                  data-testid="pricing-yearly"
                  className={`px-4 py-1.5 rounded-full font-medium transition-colors ${cadence === "yearly" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
            Yearly <span className="text-[hsl(var(--gain))] ml-1 text-xs">save up to 29%</span>
          </button>
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-6xl mx-auto">
        {PLANS.map((p) => {
          const Icon = p.icon;
          const showYearly = cadence === "yearly" && p.yearly;
          return (
            <Card key={p.id}
                  data-testid={`plan-${p.id}`}
                  className={`p-6 rounded-3xl relative transition-all hover:scale-[1.01] ${
                    p.highlight ? "border-2 border-[hsl(var(--ai))] bg-[hsl(var(--ai))]/5 md:scale-[1.03]" : ""
                  }`}>
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[hsl(var(--ai))] text-background text-[10px] uppercase tracking-widest font-bold">
                  {p.badge}
                </div>
              )}
              <Icon className="w-6 h-6 mb-3" style={{ color: p.accent }} />
              <div className="font-display text-2xl font-bold">Aanar {p.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{p.tagline}</div>
              <div className="mt-4 mb-1 flex items-baseline gap-1">
                <span className="font-mono-tab text-4xl md:text-5xl font-black" style={{ color: p.accent }}>
                  {showYearly ? p.yearlyLabel.split("/")[0] : p.priceLabel}
                </span>
                <span className="text-sm text-muted-foreground">
                  {showYearly ? "/yr" : p.cadence}
                </span>
              </div>
              {showYearly && <div className="text-xs text-[hsl(var(--gain))] font-medium mb-2">{p.yearlySave}</div>}

              <Button
                data-testid={`plan-${p.id}-cta`}
                onClick={() => {
                  if (isOwner) return;  // owner already has everything
                  if (p.ctaAction === "join") joinWaitlist(p.id);
                  else if (p.ctaLink) window.location.href = p.ctaLink;
                }}
                disabled={busy === p.id || isOwner}
                className={`w-full mt-3 rounded-full ${
                  p.highlight ? "bg-[hsl(var(--ai))] hover:bg-[hsl(var(--ai))]/90 text-background" : ""
                }`}>
                {isOwner ? "You have this" : (busy === p.id ? "Adding you…" : p.cta)}
                {!isOwner && <ArrowRight className="w-4 h-4 ml-1.5" />}
              </Button>

              <ul className="space-y-2 mt-5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    {f.in
                      ? <Check className="w-4 h-4 text-[hsl(var(--gain))] mt-0.5 shrink-0" />
                      : <X className="w-4 h-4 text-muted-foreground/50 mt-0.5 shrink-0" />}
                    <span className={f.in ? "" : "text-muted-foreground line-through"}>{f.t}</span>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>

      {/* Founder deal callout */}
      <Card className="p-6 md:p-8 rounded-3xl max-w-4xl mx-auto border-2 border-[hsl(var(--gain))]/40 bg-gradient-to-br from-[hsl(var(--gain))]/10 to-transparent" data-testid="pricing-founder-callout">
        <div className="flex items-start gap-4 flex-wrap">
          <Trophy className="w-8 h-8 text-[hsl(var(--gain))]" />
          <div className="flex-1 min-w-[250px]">
            <div className="font-display text-2xl font-bold">Founder Lifetime Deal</div>
            <p className="text-sm text-muted-foreground mt-1">
              Pay <span className="text-foreground font-mono-tab">$299 once</span> and get <span className="text-foreground font-semibold">Aanar Plus for life</span>. Limited to the first 100 traders — you'll be part of the founding community and grandfathered into every future Plus feature at no extra cost.
            </p>
          </div>
          {isOwner ? (
            <Button size="lg" data-testid="pricing-founder-cta" disabled
                    className="rounded-full bg-[hsl(var(--gain))]/50 text-background font-bold cursor-not-allowed">
              You have this
            </Button>
          ) : (
            <Link to="/founder">
              <Button size="lg" data-testid="pricing-founder-cta"
                      className="rounded-full bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-background font-bold">
                Claim my spot <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
        </div>
      </Card>

      {/* FAQ */}
      <div className="max-w-3xl mx-auto space-y-3" data-testid="pricing-faq">
        {[
          { q: "Do I need a credit card to start?",
            a: "No. Free tier gives you paper trading with $100k virtual cash and Copilot for free, no card required." },
          { q: "How does Aanar make money if it's not selling my order flow?",
            a: "Aanar doesn't sell payment for order flow (PFOF) — unlike Robinhood. Our revenue comes only from Plus and Elite subscriptions and a small platform fee on publisher signal payouts." },
          { q: "Can I cancel anytime?",
            a: "Yes. Cancel from Settings → Billing. Your Plus features stay active through the end of your paid period, then downgrade to Free automatically." },
          { q: "What brokers does Aanar route live trades through?",
            a: "Interactive Brokers (IBKR), Alpaca Markets, and Tradier. You bring your own broker account — Aanar never holds your money." },
          { q: "Is my data private?",
            a: "Your positions, orders, and API keys are encrypted at rest. Aanar never shares individual trading data with third parties. Anonymized aggregate stats power the public leaderboard." },
        ].map((f, i) => (
          <details key={i} className="p-4 rounded-2xl border border-border hover:bg-secondary/30 transition-colors" data-testid={`faq-${i}`}>
            <summary className="font-medium cursor-pointer">{f.q}</summary>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}
